import { profilePicAxios, toaAxios } from './axios';
import config from '../config';
import { NewTeam } from 'redux/models/newTeam';
import { NewPost } from 'redux/models/newPost';
import { NewSubpost } from 'redux/models/newSubpost';
import { GraphGroupUser } from 'redux/models/graphGroupUser';
import { EditTeam } from 'redux/models/editTeam';
import { TeamUsers } from 'redux/models/teamUsers';
import { Feedback } from 'redux/models/feedback';
import { EditSeason } from 'redux/models/editSeason';
import { ShoutOut } from 'redux/models/shoutOut';
import { GetPaginatedPosts } from 'redux/models/paginatedPosts';

export async function getUsers(teamId: string) {
	const url = `${config.apiBaseUri}users/forTeam/${teamId}`;
	const result = await toaAxios.get(url);
	return result.data;
}
export async function getTeams() {
	const result = await toaAxios.get('https://jsonplaceholder.typicode.com/users');
	return result.data;
}

export async function getMe() {
	const result = await toaAxios.get('me');
	return result.data;
}

export async function getProfilePic(userEmail: string) {
	const result = await profilePicAxios.get(userEmail, { responseType: 'arraybuffer' });
	const image = btoa(new Uint8Array(result.data).reduce((data, byte) => data + String.fromCharCode(byte), ''));
	return `data:${result.headers['content-type'].toLowerCase()};base64,${image}`;
}

export async function getAwardsForUserForTeam(teamId: string, userId: string) {
	const result = await toaAxios.get(`awards/ForUser/${userId}/ForTeam/${teamId}`);
	return result.data;
}

export async function getTeamNames(searchString: string) {
	const url = `${config.apiBaseUri}teamnames/${searchString}`;
	const result = await toaAxios.get(url);
	return result.data;
}

export async function getPostsForTeam(teamId: string) {
	const url = `${config.apiBaseUri}posts/forTeam/${teamId}`;
	const result = await toaAxios.get(url);
	return result.data;
}

export async function getPost(postId: string) {
	const url = `${config.apiBaseUri}posts/${postId}`;
	const result = await toaAxios.get(url);
	return result.data;
}

export async function getPaginatedPostsForTeam(teamId: string, pageSize: number = 25, continuationToken: any = null) {
	const url = `${config.apiBaseUri}posts/forTeam/${teamId}/paginated`;
	let getPaginatedPosts: GetPaginatedPosts = {
		teamId: teamId,
		pageSize: pageSize,
		token: continuationToken,
	};
	const result = await toaAxios.post(url, getPaginatedPosts);
	return result.data;
}

export async function getSubpostsForPost(postId: string) {
	const url = `${config.apiBaseUri}subposts/forPost/${postId}`;
	const result = await toaAxios.get(url);
	return result.data;
}

export async function getTeamNameAvailability(teamName: string) {
	const url = `${config.apiBaseUri}teamNames/${teamName}/availability`;
	const result = await toaAxios.get(url);
	return result.data.value;
}

export async function joinTeam(teamId: string) {
	const result = await toaAxios.post(`${config.apiBaseUri}teams/${teamId}/join`);
	return result.data;
}

export async function createTeam(team: NewTeam) {
	const result = await toaAxios.post(`${config.apiBaseUri}teams`, team);
	return result.data;
}

export async function editTeam(team: EditTeam) {
	const result = await toaAxios.put(`${config.apiBaseUri}teams/${team.teamId}`, team);
	return result.data;
}

export async function favoriteTeam(teamId: string) {
	const result = await toaAxios.post(`${config.apiBaseUri}teams/${teamId}/favorite`);
	return result.data;
}

export async function sendTokens(newPost: NewPost) {
	const result = await toaAxios.post('posts', newPost);
	return result.data;
}

export async function sendSubpostTokens(newSubpost: NewSubpost) {
	const result = await toaAxios.post('subposts', newSubpost);
	return result.data;
}

export async function leaveTeam(teamId: string) {
	const result = await toaAxios.post(`teams/${teamId}/leave`);
	return result.data;
}

export async function likePost(teamId: string, postId: string) {
	const result = await toaAxios.post(`teams/${teamId}/posts/${postId}/likes`);
	return result.data;
}

export async function likeSubpost(postId: string, subpostId: string) {
	const result = await toaAxios.post(`posts/${postId}/subposts/${subpostId}/likes`);
	return result.data;
}

export async function searchUsersFromGraph(searchString: string) {
	const url = `${config.apiBaseUri}users/${searchString}/graph`;
	const result = await toaAxios.get(url);
	return result.data;
}

export async function addTeamMembers(teamId: string, newMembers: GraphGroupUser[]) {
	const url = `${config.apiBaseUri}teams/${teamId}/members`;
	const result = await toaAxios.post(url, newMembers);
	return result.data;
}

export async function deleteTeam(teamId: string) {
	const result = await toaAxios.delete(`teams/${teamId}`);
	return result.data;
}

export async function updateUsersByTeam(teamId: string, users: TeamUsers[]) {
	const result = await toaAxios.put(`teams/${teamId}/users`, users);
	return result.data;
}

export async function deleteUsersByTeam(teamId: string, userIds: string[]) {
	const result = await toaAxios.delete(`teams/${teamId}/users`, {
		data: userIds,
	});
	return result.data;
}

export async function sendFeedback(feedback: Feedback) {
	const result = await toaAxios.post('feedback', feedback);
	return result.data;
}

export async function acceptEula() {
	const result = await toaAxios.post('acceptTerms');
	return result.data;
}

export async function toggleAllNotifcations(teamId: string) {
	const result = await toaAxios.post(`teams/${teamId}/allnotifications`);
	return result.data;
}

export async function togglePersonalNotifcations(teamId: string) {
	const result = await toaAxios.post(`teams/${teamId}/personalnotifications`);
	return result.data;
}

export async function updateSeason(season: EditSeason) {
	const result = await toaAxios.put(`teams/${season.teamId}/season/${season.seasonId}`, season);
	return result.data;
}

export async function endSeason(season: EditSeason) {
	const result = await toaAxios.post(`teams/${season.teamId}/season/${season.seasonId}/end`, season);
	return result.data;
}

export async function startSeason(teamId: string) {
	const result = await toaAxios.post(`teams/${teamId}/season/start`);
	return result.data;
}

export async function getSeasonsForTeam(teamId: string) {
	const result = await toaAxios.get(`seasons/forTeam/${teamId}`);
	return result.data;
}

export async function getTeamSeasonBadges(teamId: string, seasonId: string) {
	const result = await toaAxios.get(`badges/forTeam/${teamId}/forSeason/${seasonId}`);
	return result.data;
}

export async function deletePost(postId: string) {
	const result = await toaAxios.delete(`post/${postId}`);
	return result.data;
}

export async function deleteSubpost(subpostId: string) {
	const result = await toaAxios.delete(`subpost/${subpostId}`);
	return result.data;
}

export async function getTeamUserStats(teamId: string, userId: string) {
	const result = await toaAxios.get(`stats/forUser/${userId}/forTeam/${teamId}`);
	return result.data;
}

export async function saveTour(tourName: string) {
	const result = await toaAxios.post('completedTour', { name: tourName });
	return result.data;
}

export async function resetTours() {
	const result = await toaAxios.delete('deleteTours');
	return result.data;
}

export async function sendShoutOut(shoutout: ShoutOut) {
	const result = await toaAxios.post(`shoutout`, shoutout);
	return result.data;
}

export async function userHasAcceptedTerms(userId: string) {
	const result = await toaAxios.get(`user/${userId}/hasAcceptedTerms`);
	return result.data.value;
}

export async function getEasterEggs() {
	const result = await toaAxios.get('easterEggs');
	return result.data;
}

export async function getEasterEggsForUser(userId: string) {
	const result = await toaAxios.get(`easterEggsForUser/${userId}`);
	return result.data;
}

export async function postEasterEggFound(eggId: string) {
	const result = await toaAxios.post(`foundEasterEgg/${eggId}`);
	return result.data;
}

export async function getAmIMasterEggHunter() {
	const result = await toaAxios.get(`amIMasterEggHunter`);
	return result.data;
}

export async function getMasterEggHunters() {
	const result = await toaAxios.get(`masterEggHunters`);
	return result.data;
}
